import { useState, useEffect, useMemo } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  MapPinIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import { getTournamentEvents, getTournaments } from '../../lib/supabase';

interface Event {
  id: string;
  title: string;
  description?: string;
  date: string;
  time?: string;
  location?: string;
  event_type?: string;
}

function toLocalDateStr(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

export default function MobileEventCalendar() {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data: tournaments } = await getTournaments();
      const upcoming = tournaments?.find((t: { status: string }) => t.status === 'upcoming')
        || [...(tournaments || [])].sort((a, b) => b.start_date?.localeCompare(a.start_date))[0];
      if (upcoming?.id) {
        const { data } = await getTournamentEvents(upcoming.id);
        const loaded: Event[] = (data || []).map((e: Event) => ({
          ...e,
          date: e.date || '',
        }));
        setEvents(loaded);

        if (loaded.length > 0) {
          const todayStr = toLocalDateStr(new Date());
          const hasTodayEvents = loaded.some(e => e.date === todayStr);
          if (!hasTodayEvents) {
            const future = loaded.map(e => e.date).filter(d => d >= todayStr).sort();
            const past = loaded.map(e => e.date).filter(d => d < todayStr).sort().reverse();
            const nearest = future[0] || past[0];
            if (nearest) {
              const [y, m, d] = nearest.split('-').map(Number);
              const nearestDate = new Date(y, m - 1, d);
              setCurrentDate(nearestDate);
              setCalendarMonth(new Date(y, m - 1, 1));
            }
          }
        }
      }
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const eventDates = useMemo(() => new Set(events.map(e => e.date)), [events]);

  const monthCalendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: (Date | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
    return days;
  }, [calendarMonth]);

  const selectedDateStr = toLocalDateStr(currentDate);
  const dayEvents = events.filter(e => e.date === selectedDateStr);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="px-4 pt-2 pb-8">
      {/* Compact mini calendar */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 p-3">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4 text-gray-400" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-golf-gray">
              {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={() => {
                setCurrentDate(new Date());
                setCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1));
              }}
              className="px-2 py-0.5 text-[10px] font-medium rounded border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
            >
              Today
            </button>
          </div>
          <button
            onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRightIcon className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="grid grid-cols-7 mb-0.5">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="text-center text-[10px] font-medium text-gray-400 py-0.5">{day}</div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {monthCalendarDays.map((date, i) => {
            if (!date) return <div key={`empty-${i}`} />;
            const dateStr = toLocalDateStr(date);
            const hasEvents = eventDates.has(dateStr);
            const isSelected = isSameDay(date, currentDate);
            const isToday = isSameDay(date, today);
            return (
              <button
                key={i}
                onClick={() => setCurrentDate(date)}
                className={`relative flex flex-col items-center justify-center rounded-lg text-xs font-medium transition-all py-1.5 ${
                  isSelected
                    ? 'bg-golf-orange text-white shadow-sm'
                    : isToday
                    ? 'bg-orange-50 text-golf-orange border border-golf-orange/40'
                    : hasEvents
                    ? 'text-gray-800 font-semibold hover:bg-gray-50'
                    : 'text-gray-400 hover:bg-gray-50'
                }`}
              >
                {date.getDate()}
                {hasEvents && (
                  <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-golf-orange'}`} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day label */}
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
        {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
      </p>

      {/* Events for selected day */}
      {dayEvents.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <CalendarDaysIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No events on this day</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {dayEvents.map(event => {
            let timeLabel = '';
            if (event.time) {
              try {
                timeLabel = format(parseISO(`2000-01-01T${event.time}`), 'h:mm a');
              } catch {
                timeLabel = event.time;
              }
            }
            const calStartTime = event.time ? event.time.substring(0, 5) : '00:00';
            const calEndHour = event.time ? String(Math.min(parseInt(event.time.split(':')[0]) + 1, 23)).padStart(2, '0') : '01';
            const calEndTime = event.time ? `${calEndHour}:${event.time.split(':')[1] || '00'}` : '01:00';

            return (
              <div key={event.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                <p className="font-semibold text-golf-gray text-sm mb-1">{event.title}</p>
                {event.description && (
                  <p className="text-xs text-gray-500 mb-2 leading-relaxed">{event.description}</p>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
                  {event.time && (
                    <span className="flex items-center gap-1 text-xs text-golf-orange font-medium">
                      <ClockIcon className="w-3.5 h-3.5" />
                      {timeLabel || event.time}
                    </span>
                  )}
                  {event.location && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <MapPinIcon className="w-3.5 h-3.5" />
                      {event.location}
                    </span>
                  )}
                </div>
                <div className="atcb-mobile-wrapper">
                  <AddToCalendarButton
                    name={event.title}
                    description={event.description || ''}
                    startDate={event.date}
                    startTime={calStartTime}
                    endTime={calEndTime}
                    location={event.location || ''}
                    options={['Apple', 'Google', 'Outlook.com', 'iCal']}
                    timeZone="Africa/Lagos"
                    buttonStyle="flat"
                    size="2"
                    label="Add to Calendar"
                    lightMode="light"
                    hideTextLabelButton={false}
                    hideCheckmark
                    styleLight="--btn-background: transparent; --btn-border: 1px solid #e5e7eb; --btn-text: #ea580c; --btn-font-weight: 600; --btn-shadow: none; --btn-hover-background: #fff7ed; --btn-hover-border: 1px solid #ea580c; --btn-hover-text: #ea580c; --btn-padding-x: 12px; --btn-padding-y: 6px; --font: inherit;"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
